import React, { useState, useMemo } from "react";
import ReactBeforeSlider from "../ReactBeforeSlider/ReactBeforeSlider";
import Modal from "../Modal/Modal";
import useCategoryData from "../CategoryData/CategoryData"; // Import the custom hook

function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState("poele");
  const [selectedSubcategory, setSelectedSubcategory] = useState("poele-bois");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalImageIndex, setModalImageIndex] = useState(null);

  const imagesPerPage = 20; // Number of images per page

  const categories = [
    { id: "poele", name: "Poêle", subcategories: ["poele-bois", "poele-pellet", "poele-gaz", "tubage"] },
    { id: "chauffage", name: "Chauffage", subcategories: ["chaudiere", "radiateur"] },
    { id: "cuisine", name: "Cuisine" },
    { id: "salle-de-bain", name: "Salle de bain" },
    { id: "sanitaire", name: "Sanitaire", subcategories: ["robinet", "evier", "meuble-vasque", "chauffe-eau", "wc"] },
  ];

  const subcategories = [
    { id: "poele-bois", name: "Poêle à bois" },
    { id: "poele-pellet", name: "Poêle à pellet" },
    { id: "poele-gaz", name: "Poêle à gaz" },
    { id: "tubage", name: "Tubage" },
    { id: "chaudiere", name: "Chaudière" },
    { id: "radiateur", name: "Radiateur" },
    { id: "robinet", name: "Robinet" },
    { id: "evier", name: "Evier" },
    { id: "meuble-vasque", name: "Meuble vasque" },
    { id: "chauffe-eau", name: "Chauffe-eau électrique" },
    { id: "wc", name: "WC" },
  ];

  // Fetch data using the custom hook
  const { data: allImages, loading, error } = useCategoryData(selectedCategory, selectedSubcategory);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    const category = categories.find((cat) => cat.id === categoryId);
    // If "Tous" category is selected, reset subcategory to null
    if (categoryId === "tous") {
      setSelectedSubcategory(null);
    } else {
      // If a category with subcategories is selected, set the first subcategory as default
      setSelectedSubcategory(category?.subcategories?.[0] || null);
    }
    setCurrentPage(1); // Reset to first page
  };

  const paginatedImages = useMemo(() => {
    if (loading || error) return [];
    const startIndex = (currentPage - 1) * imagesPerPage;
    const endIndex = startIndex + imagesPerPage;
    return allImages.slice(startIndex, endIndex);
  }, [allImages, currentPage, loading, error]);

  const totalPages = Math.ceil(allImages.length / imagesPerPage);

  const openModal = (index) => {
    setModalImageIndex(index);
  };

  const closeModal = () => {
    setModalImageIndex(null);
  };

  const showPreviousImage = () => {
    setModalImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const showNextImage = () => {
    setModalImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <nav className="gallerie-category">
        <ul className="gallerie-list">
          {categories.map((category) => (
            <React.Fragment key={category.id}>
              <div className="main-category">
                <li
                  className={`gallerie-item ${selectedCategory === category.id ? "active" : ""} ${
                    category.subcategories ? "has-subcategories" : ""
                  }`}
                  onClick={() => handleCategoryClick(category.id)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select category: ${category.name}`}>
                  <span>{category.name}</span>
                </li>
                {category.subcategories && selectedCategory === category.id && (
                  <ul className="gallerie-subcategories">
                    {category.subcategories.map((subcategoryId) => {
                      const subcategory = subcategories.find((sub) => sub.id === subcategoryId);
                      return (
                        <li
                          key={subcategory.id}
                          className={`gallerie-item ${selectedSubcategory === subcategory.id ? "active" : ""}`}
                          onClick={() => {
                            setSelectedSubcategory(subcategory.id);
                            setCurrentPage(1); // Reset to first page
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Select subcategory: ${subcategory.name}`}>
                          <span>{subcategory.name}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </React.Fragment>
          ))}
        </ul>
      </nav>

      <div className="gallerie-images">
        {loading && <p>Chargement...</p>}
        {error && <p>Error loading images: {error}</p>}
        {!loading && !error && paginatedImages.length === 0 && <p>No images found</p>}
        {!loading &&
          !error &&
          paginatedImages.map((image, index) => (
            <div key={index} className="gallerie-image">
              <i
                className="fa-solid fa-arrow-up-right-from-square icon-bigger"
                onClick={() => openModal((currentPage - 1) * imagesPerPage + index)}
              />
              {/* Check if there are before-and-after images */}
              {image.before && image.after ? (
                <ReactBeforeSlider
                  firstImage={{ imageUrl: image.before, alt: image.alt }}
                  secondImage={{ imageUrl: image.after, alt: image.alt }}
                />
              ) : (
                <img src={image.src} alt={image.alt} />
              )}
            </div>
          ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              className={`pagination-button ${currentPage === index + 1 ? "active" : ""}`}
              onClick={() => {
                setCurrentPage(index + 1);
                scrollToTop();
              }}>
              {index + 1}
            </button>
          ))}
        </div>
      )}

      <Modal
        image={allImages[modalImageIndex]}
        closeModal={closeModal}
        showPreviousImage={showPreviousImage}
        showNextImage={showNextImage}
      />
    </>
  );
}

export default Gallery;
